import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { getTranslatedKey } from "../../../utils/commonfunction";
import { capOf, unitsOf } from "../../../utils/addonQuantity";
import SpiceLevelChips from "./SpiceLevelChips";

// `commonfunction` is untyped JS whose `data` parameter defaults to null, so ts
// infers the parameter as `null`. One cast here rather than at every call.
const translated = (key: string, data: unknown): string =>
  getTranslatedKey(key, data as never);

export interface SpiceOption {
  _id: string;
  name?: string;
  [key: string]: unknown;
}

export interface AddonChoice {
  _id: string;
  item_name?: string;
  item_name_ja?: string;
  item_variation?: unknown;
  price?: number;
  tax_price?: number;
  total_price?: number;
  spice_level_needed?: boolean;
  /** Units of THIS option one line may take. Null / absent is no ceiling. */
  max_quantity?: number | null;
  [key: string]: unknown;
}

/** A group as the menu configures it: the immutable catalogue side. */
export interface AddonGroup {
  _id?: string;
  addon_name?: string;
  items?: AddonChoice[];
  /** Units the line may take from this group in total. Null / absent is no
   *  ceiling. Counted across every option, the way the backend counts it. */
  max_choice?: number | null;
  [key: string]: unknown;
}

/** A row on the line — one chosen option, and one payload entry. */
export interface AddonRowValue {
  item_id?: string;
  quantity?: number;
  spice_level?: SpiceOption | null;
  addon_id?: string;
  [key: string]: unknown;
}

interface Props {
  /** The item's configured groups. Decides which sections show at all. */
  groups: AddonGroup[];
  /** The row list — `formik.values.addons`, in payload order. */
  rows: AddonRowValue[];
  spiceOptions?: SpiceOption[];
  /**
   * Receives an updater, not a finished list: two steps dispatched inside one
   * React batch would both read the same rendered `rows` and the second would
   * drop the first. The caller re-prices and stores what the updater returns.
   */
  onChangeRows: (update: (current: AddonRowValue[]) => AddonRowValue[]) => void;
  /**
   * Off for bulk settlement, which records a PAST sale and prints no kitchen
   * ticket: there is no one left to cook to a heat, so nothing is asked and no
   * level is written.
   */
  withSpice?: boolean;
}

/** An unselected row of a group: the shape the caller seeds the form with, and
 *  what keeps `item_id` empty so the caller's required-field validation still
 *  blocks submit while a group is unsatisfied. */
const blankRow = (group: AddonGroup): AddonRowValue => ({
  item_id: "",
  item_name: "",
  item_name_ja: "",
  item_variation: null,
  price: 0,
  tax_price: 0,
  total_price: 0,
  spice_level: null,
  spice_level_needed: false,
  quantity: 1,
  addon_id: group?._id,
  addon_name: group?.addon_name,
});

/**
 * The row list an item starts on: one empty row per configured group entry, so
 * a group the menu lists twice starts owing two components. Every caller seeds
 * the same way — the qr sheets from the item detail, the cashier's dialog from
 * the same endpoint's groups.
 */
export const seedAddonRows = (groups: AddonGroup[] = []): AddonRowValue[] =>
  (groups || []).map(blankRow);

/** Everything price-affecting is copied onto the row, because the row IS the
 *  payload entry — the backend prices (group, item), not a reference back to
 *  the catalogue. */
const chosenRow = (
  group: AddonGroup,
  option: AddonChoice,
  quantity: number,
  spice: SpiceOption | null
): AddonRowValue => ({
  ...blankRow(group),
  item_id: option?._id,
  item_name: option?.item_name || "",
  item_name_ja: option?.item_name_ja || "",
  item_variation: option?.item_variation ?? null,
  price: option?.price || 0,
  tax_price: option?.tax_price || 0,
  total_price: option?.total_price || 0,
  spice_level_needed: option?.spice_level_needed || false,
  // Nothing is defaulted: an option that needs spice is only ever built from
  // the prompt, which does not close until the diner has chosen a level.
  spice_level: option?.spice_level_needed ? spice : null,
  quantity,
});

/**
 * The set-menu choice picker: one collapsible section per configured group,
 * listing every option of that group as its own row.
 *
 * Rendered on BOTH qr screens — the add sheet (addMenu) and the edit modal on
 * the order summary (reviewOrder), which the waiter app loads in its webview.
 * One component, so a change to how a set line is chosen cannot reach one
 * screen and miss the other.
 *
 * Purely presentational: it computes the next row list and hands the whole
 * array back through `onChangeRows`. The two screens price and submit a line
 * differently, and that stays theirs.
 *
 * `rows` IS the payload order. A group's rows are rewritten in place, so rows
 * of other groups never move and never change index.
 */
const SetMenuAddonPicker = ({
  groups = [],
  rows = [],
  spiceOptions = [],
  onChangeRows,
  withSpice = true,
}: Props) => {
  const { t } = useTranslation();
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  // The option waiting on a spice level. Nothing is written to the row list
  // until it is answered, so closing the prompt leaves the set untouched.
  const [spicePrompt, setSpicePrompt] = useState<{
    group: AddonGroup;
    option: AddonChoice;
    /**
     * Going up offers every level; coming down offers only what was taken.
     * `select` is a one-of group's radio: it offers every level like `add`, but
     * what it writes REPLACES the group rather than adding to it.
     */
    mode: "add" | "remove" | "select";
  } | null>(null);

  // Distinct groups, in configured order. A group the menu lists twice gets ONE
  // section — the repeat is what says how many components it takes, below.
  const seen = new Set<string>();
  const sections = groups.filter((group) => {
    const key = String(group?._id);
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });

  if (!sections.length) {
    return null;
  }

  // Rewrites one group's block of rows, leaving every other row — including
  // rows of groups this item does not configure — exactly where it was.
  const commit = (
    current: AddonRowValue[],
    group: AddonGroup,
    nextGroupRows: AddonRowValue[]
  ): AddonRowValue[] => {
    const groupId = String(group?._id);
    const next: AddonRowValue[] = [];
    let placed = false;
    current.forEach((row) => {
      if (String(row?.addon_id) !== groupId) {
        next.push(row);
        return;
      }
      if (!placed) {
        next.push(...nextGroupRows);
        placed = true;
      }
    });
    if (!placed) {
      next.push(...nextGroupRows);
    }
    return next;
  };

  // A group short of its configured component count keeps one empty row, which
  // is what the caller's `item_id` required-rule sees — the picker does not
  // need a validation rule of its own.
  const withFloor = (
    group: AddonGroup,
    picked: AddonRowValue[],
    slots: number
  ): AddonRowValue[] => {
    const units = picked.reduce((total, row) => total + unitsOf(row), 0);
    return units >= slots ? picked : [...picked, blankRow(group)];
  };

  /** The rows of one group that hold a choice, in payload order. */
  const pickedOf = (source: AddonRowValue[], group: AddonGroup) =>
    source.filter(
      (row) => String(row?.addon_id) === String(group?._id) && row?.item_id
    );

  const commitStep = (
    current: AddonRowValue[],
    group: AddonGroup,
    next: AddonRowValue[]
  ): AddonRowValue[] => {
    const slots = groups.filter(
      (entry) => String(entry?._id) === String(group?._id)
    ).length;
    return commit(current, group, withFloor(group, next, slots));
  };

  /** One unit on. A spiced option holds a row PER LEVEL, because two units of
   *  one dish at two heats are two different things to cook — and the order
   *  payload already keys on spice, so they never merge back together. */
  const addUnit = (
    group: AddonGroup,
    option: AddonChoice,
    spice: SpiceOption | null
  ) => {
    onChangeRows((current) => {
      const picked = pickedOf(current, group);
      const existing = picked.find(
        (row) =>
          row?.item_id === option?._id &&
          String(row?.spice_level?._id || "") === String(spice?._id || "")
      );
      const next = existing
        ? picked.map((row) =>
            row === existing ? { ...row, quantity: unitsOf(row) + 1 } : row
          )
        : [...picked, chosenRow(group, option, 1, spice)];
      return commitStep(current, group, next);
    });
  };

  /** One unit off a named level. Which level was never the picker's to guess:
   *  the diner says, on the way down as much as on the way up. */
  const removeUnit = (
    group: AddonGroup,
    option: AddonChoice,
    spice: SpiceOption | null
  ) => {
    onChangeRows((current) => {
      const picked = pickedOf(current, group);
      const target = [...picked]
        .reverse()
        .find(
          (row) =>
            row?.item_id === option?._id &&
            String(row?.spice_level?._id || "") === String(spice?._id || "")
        );
      // Nothing to take off: hand the list straight back so the caller stores
      // and re-prices exactly what it already had.
      if (!target) {
        return current;
      }
      const remaining = unitsOf(target) - 1;
      const next = picked.flatMap((row) =>
        row !== target ? [row] : remaining > 0 ? [{ ...row, quantity: remaining }] : []
      );
      return commitStep(current, group, next);
    });
  };

  /** The levels an option currently holds, each with its unit count — what the
   *  prompt offers when a unit is coming back off. */
  const levelsTaken = (group: AddonGroup, option: AddonChoice) =>
    pickedOf(rows, group)
      .filter((row) => row?.item_id === option?._id && row?.spice_level)
      .map((row) => ({ spice: row.spice_level as SpiceOption, count: unitsOf(row) }));

  // Every step of a spiced option asks, because every unit carries its own
  // level: on the way up the answer is not reusable from the unit before it,
  // and on the way down there is no telling which heat the diner meant.
  const step = (group: AddonGroup, option: AddonChoice, delta: number) => {
    // No level configured means there is nothing to ask: prompting anyway opens
    // a chip row with no chips, and the unit is never added because only
    // `takeSpice` writes it. The option adds unspiced instead.
    if (withSpice && option?.spice_level_needed && spiceOptions.length > 0) {
      setSpicePrompt({ group, option, mode: delta < 0 ? "remove" : "add" });
      return;
    }
    if (delta < 0) {
      removeUnit(group, option, null);
      return;
    }
    addUnit(group, option, null);
  };

  /**
   * The whole of a one-of group's choice: whatever it held is replaced by this
   * single unit.
   *
   * Not `addUnit` with a clear first — the group's rows are rewritten in one
   * pass, so switching between options can never leave both in the list, and a
   * second tap on the option already held is a no-op rather than a second
   * helping.
   */
  const selectUnit = (
    group: AddonGroup,
    option: AddonChoice,
    spice: SpiceOption | null
  ) => {
    onChangeRows((current) =>
      commitStep(current, group, [chosenRow(group, option, 1, spice)])
    );
  };

  /** A radio tap. Asks for a heat first where the option needs one, the same as
   *  the stepper does — the level belongs to the unit, however it was taken. */
  const choose = (group: AddonGroup, option: AddonChoice) => {
    if (withSpice && option?.spice_level_needed && spiceOptions.length > 0) {
      setSpicePrompt({ group, option, mode: "select" });
      return;
    }
    selectUnit(group, option, null);
  };

  const takeSpice = (spice: SpiceOption) => {
    if (!spicePrompt) {
      return;
    }
    const { group, option, mode } = spicePrompt;
    if (mode === "remove") {
      removeUnit(group, option, spice);
    } else if (mode === "select") {
      selectUnit(group, option, spice);
    } else {
      addUnit(group, option, spice);
    }
    setSpicePrompt(null);
  };

  return (
    <div className="set-addon-picker">
      {sections.map((group) => {
        const groupId = String(group?._id);
        // The menu listing a group N times is the FLOOR — how many components
        // the diner owes. `max_choice` is the ceiling, and the two are set
        // independently: a group listed twice with a max of 1 can never be
        // satisfied, which is the admin form's problem to warn about, not
        // something to paper over here.
        //
        // Every group still steps rather than picks. A max of 1 is the case that
        // could become radios — one tap to switch instead of minus-then-plus —
        // but that is a different control, not a cap, and the cap is what the
        // order path enforces. Left as a stepper until someone asks.
        const picked = rows.filter(
          (row) => String(row?.addon_id) === groupId && row?.item_id
        );
        // Listed N times = N components owed. Until that many units are taken
        // the group is still short, however much is already in it.
        const slots = groups.filter((entry) => String(entry?._id) === groupId).length;
        const units = picked.reduce((total, row) => total + unitsOf(row), 0);
        // What this group has added to the line, counted the way the line is:
        // a unit contributes its gross once per unit.
        const groupTotal = picked.reduce(
          (total, row) => total + (Number(row?.total_price) || 0) * unitsOf(row),
          0
        );
        const isCollapsed = !!collapsed[groupId];
        // The ceiling the order path will enforce (`assertAddonCaps`). Reached
        // here, the group's every `+` goes dead — better than letting the diner
        // build a line the order refuses on submit.
        const groupCap = capOf(group?.max_choice);
        const atGroupCap = groupCap != null && units >= groupCap;
        // A group that can hold exactly one component is a pick, not a count:
        // radios say "one of these" in the control itself, and switching is one
        // tap instead of minus-then-plus. Every other group still steps.
        const isSingleChoice = groupCap === 1;
        const RowTag = isSingleChoice ? "label" : "div";

        // Open sections are tinted: with no border left to box a group, the
        // fill is what says how far the one being worked on reaches.
        return (
          <div
            className={`set-addon-group${isCollapsed ? "" : " is-open"}`}
            key={groupId}
          >
            {/* The whole header is the toggle — a diner tapping the group name
                means the same thing as tapping the chevron. */}
            <button
              type="button"
              className="set-addon-head"
              aria-expanded={!isCollapsed}
              onClick={() =>
                setCollapsed((current) => ({ ...current, [groupId]: !current[groupId] }))
              }
            >
              <span className="set-addon-head-text">
                <span className="set-addon-title">{translated("addon_name", group)}</span>
                {/* A dead `+` with nothing explaining it reads as a bug. The
                    ceiling is stated up front, so reaching it is expected — but
                    not for a one-of group, where the radios already say it and
                    "choose up to 1" would only restate the control. */}
                {groupCap != null && !isSingleChoice ? (
                  <span className="set-addon-group-cap">
                    {t("label_choose_up_to", { max: groupCap })}
                  </span>
                ) : null}
              </span>
              {/* Right of the name, next to the chevron. It is a prompt, not a
                  property: it stands until the group holds every component it
                  owes. Once it does, the same slot carries what the group added
                  — the answer to the question the badge was asking. */}
              {units >= slots ? (
                <span className="set-addon-group-total">+¥{groupTotal}</span>
              ) : (
                <span className="set-addon-required">{t("label_required")}</span>
              )}
              <i className={isCollapsed ? "ti ti-chevron-down" : "ti ti-chevron-up"}></i>
            </button>

            {isCollapsed ? null : (
              <ul
                className="set-addon-options"
                role={isSingleChoice ? "radiogroup" : undefined}
                aria-label={isSingleChoice ? translated("addon_name", group) : undefined}
              >
                {(group?.items || []).map((option) => {
                  // One option can hold several rows — one per spice level —
                  // so the stepper counts the units across all of them.
                  const optionRows = picked.filter(
                    (entry) => entry?.item_id === option?._id
                  );
                  const count = optionRows.reduce(
                    (total, entry) => total + unitsOf(entry),
                    0
                  );
                  // Sold out is the cashier's case, but it is a property of the
                  // option, not of the till — the diner must not be able to take
                  // one either.
                  const soldOut = option?.is_available === false;
                  // Two independent ceilings: the group's budget can still have
                  // room while this one option is spent, and vice versa.
                  const optionCap = capOf(option?.max_quantity);
                  const atOptionCap = optionCap != null && count >= optionCap;
                  return (
                    <li
                      className={
                        [
                          "set-addon-option",
                          count ? "is-picked" : "",
                          soldOut ? "is-sold-out" : "",
                        ]
                          .filter(Boolean)
                          .join(" ")
                      }
                      key={String(option?._id)}
                    >
                      {/* A one-of row is a LABEL wrapping its radio, so the
                          whole line is the target — name, price and all — with
                          no click handler of our own and nothing for a keyboard
                          or a screen reader to lose. Stepper rows stay a plain
                          div: they hold two buttons, and a label around those
                          would make every tap on the row hit one of them. */}
                      <RowTag
                        className={`set-addon-option-row${
                          isSingleChoice && !soldOut ? " is-selectable" : ""
                        }`}
                      >
                        <span className="set-addon-option-text">
                          {/* Name then price on one line, the price in a column
                              of its own so every row's price lines up whatever
                              the name's length. */}
                          <span className="set-addon-option-main">
                            <span className="set-addon-option-name">
                              {translated("item_name", option)}
                              {soldOut ? (
                                <span className="set-addon-sold-out">
                                  {t("sold_out")}
                                </span>
                              ) : null}
                            </span>
                            {/* A free option prints no price — a `¥0` on every
                                row is noise, and the old `-¥0` read as a
                                decrement. The column stays either way, so the
                                priced rows still align. */}
                            <span className="set-addon-option-price">
                              {option?.total_price ? `+¥${option.total_price}` : ""}
                            </span>
                          </span>
                          {/* Says why THIS option's `+` went dead, on the row
                              it went dead on — the group's own ceiling is
                              announced in the header instead, so the two never
                              both speak for one button. Never in a one-of
                              group: there the count tops out at one anyway, and
                              "max reached" beside a radio the diner just picked
                              reads as an error.

                              Under the name rather than beside it: the name's
                              line already carries the price and clears the
                              stepper, and a phone has no width to lend. Same
                              slot as the spice tally, which is the row's
                              existing place for what was chosen rather than
                              what is offered. */}
                          {!isSingleChoice && atOptionCap ? (
                            <span className="set-addon-max-reached">
                              {t("label_max_reached")}
                            </span>
                          ) : null}
                          {/* What was taken, level by level — "medium ×2" over
                              "low ×1". The tally IS the record of the choice;
                              the chips only live in the prompt. Rows with no
                              level have nothing to tally: bulk settlement runs
                              `withSpice` off and never writes one. */}
                          {option?.spice_level_needed
                            ? optionRows
                                .filter((entry) => entry?.spice_level)
                                .map((entry, entryIndex) => (
                                  <span
                                    className="set-addon-spice-tally"
                                    key={`${String(entry?.spice_level?._id)}-${entryIndex}`}
                                  >
                                    {t("spice_level_text")}:{" "}
                                    {translated("name", entry.spice_level) ||
                                      entry.spice_level?.name}{" "}
                                    ×{unitsOf(entry)}
                                  </span>
                                ))
                            : null}
                        </span>

                        {isSingleChoice ? (
                          // Never disabled by the group's own ceiling: a radio
                          // IS the group at its ceiling, and disabling the
                          // unselected ones would make the choice unchangeable.
                          <div className="set-addon-radio">
                            <input
                              type="radio"
                              name={`set-addon-choice-${groupId}`}
                              aria-label={translated("item_name", option)}
                              checked={count > 0}
                              disabled={soldOut}
                              onChange={() => choose(group, option)}
                            />
                          </div>
                        ) : (
                          <div className="set-addon-stepper">
                            <button
                              type="button"
                              className="btn btn-sm"
                              aria-label={t("remove")}
                              disabled={!count}
                              onClick={() => step(group, option, -1)}
                            >
                              <i className="ti ti-minus"></i>
                            </button>
                            <span className="set-addon-count">{count}</span>
                            {/* A sold-out or capped option can still be given
                                back — what it cannot be is taken. */}
                            <button
                              type="button"
                              className="btn btn-sm"
                              aria-label={t("add_options")}
                              disabled={soldOut || atGroupCap || atOptionCap}
                              onClick={() => step(group, option, 1)}
                            >
                              <i className="ti ti-plus"></i>
                            </button>
                          </div>
                        )}
                      </RowTag>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        );
      })}

      {/* Asked, not defaulted: no level is preselected, and tapping away writes
          nothing — the unit the prompt was opened for is simply not taken, so
          there is no cancel to press. Its own overlay rather than a bootstrap
          modal, because the picker is already inside one on both screens it
          renders on. */}
      {spicePrompt ? (
        <div className="set-addon-prompt" role="dialog" aria-modal="true">
          <div
            className="set-addon-prompt-backdrop"
            onClick={() => setSpicePrompt(null)}
          ></div>
          <div className="set-addon-prompt-panel">
            {/* The title says which way the tap went, so the same panel cannot
                be mistaken for the other one. */}
            <div className="set-addon-prompt-head">
              <span className="set-addon-prompt-title">
                {spicePrompt.mode === "remove"
                  ? t("label_remove_spice_level")
                  : t("label_add_spice_level")}
              </span>
              <span className="set-addon-prompt-item">
                {translated("item_name", spicePrompt.option)}
              </span>
            </div>
            {/* Going up offers the whole menu of levels; coming down offers
                only the ones this option is actually holding, each with what
                it holds — there is nothing to take off a level never taken. */}
            {spicePrompt.mode === "remove" ? (
              <SpiceLevelChips
                name="set_addon_spice_prompt"
                options={levelsTaken(spicePrompt.group, spicePrompt.option).map(
                  (taken) => taken.spice
                )}
                counts={levelsTaken(spicePrompt.group, spicePrompt.option).reduce(
                  (counts, taken) => ({
                    ...counts,
                    [String(taken.spice?._id)]: taken.count,
                  }),
                  {} as Record<string, number>
                )}
                onPick={takeSpice}
              />
            ) : (
              <SpiceLevelChips
                name="set_addon_spice_prompt"
                options={spiceOptions}
                onPick={takeSpice}
              />
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default SetMenuAddonPicker;
